import { useMemo, useState } from "react";
import { ArrowRight, BookOpen, Brain, Heart, Mic, Search, Sparkles, Target, Zap } from "lucide-react";
import { VideoCard } from "@/components/content/VideoCard";
import { useWorkspace } from "@/stores/workspace";
import { cn } from "@/lib/cn";
import { resourcesForTopic } from "@/lib/gmat-resources";
import { unlockAudio } from "@/lib/tts";
import {
  ALL_GMAT_TOPICS,
  DI_TOPICS,
  GMAT_SECTION_LABELS,
  MINDSET_TOPICS,
  QUANT_TOPICS,
  VERBAL_TOPICS,
  buildCustomLessonPrompt,
  buildLessonPrompt,
  getTopicById,
  recommendedTopics,
  searchTopics,
  type GmatSection,
  type GmatTopic,
} from "@/lib/gmat-topics";

const MINDSET_ICONS: Record<string, typeof Heart> = {
  "m-anxiety": Heart,
  "m-pacing": Zap,
  "m-plateau": Target,
  "m-review": Brain,
  "m-study-plan": BookOpen,
};

type SectionFilter = GmatSection | "all";

export function TopicBrowser() {
  const [searchQuery, setSearchQuery] = useState("");
  const [askQuery, setAskQuery] = useState("");
  const [section, setSection] = useState<SectionFilter>("all");
  const [customTopic, setCustomTopic] = useState("");
  const startTutorSession = useWorkspace((s) => s.startTutorSession);
  const askMaya = useWorkspace((s) => s.askMaya);

  const filtered = useMemo(() => {
    let list = searchTopics(searchQuery);
    if (section !== "all") list = list.filter((t) => t.section === section);
    return list;
  }, [searchQuery, section]);

  const launchTutor = (topic: GmatTopic) => {
    void unlockAudio();
    startTutorSession({
      topicId: topic.id,
      topicName: topic.name,
      section: topic.section,
      lessonPrompt: buildLessonPrompt(topic),
    });
  };

  const launchCustomTutor = () => {
    const t = customTopic.trim();
    if (!t) return;
    void unlockAudio();
    startTutorSession({
      topicName: t,
      section: "custom",
      lessonPrompt: buildCustomLessonPrompt(t),
    });
    setCustomTopic("");
  };

  const recommends = recommendedTopics(2);

  return (
    <div className="container space-y-10 py-10 pb-36">
      <header>
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" /> Topics
        </div>
        <h1 className="mt-3 font-serif text-5xl font-normal leading-[0.98] tracking-tight md:text-6xl">
          Pick a topic — <span className="italic text-primary">Maya teaches live</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Each topic opens a 1-on-1 tutoring session: step-by-step, voice-first, like a real tutor.
          For quick questions (study plans, scores, tips), use{" "}
          <strong className="font-medium text-foreground">Ask Maya</strong> in the sidebar — that's the chatbot.
        </p>
      </header>

      {/* Custom topic */}
      <section className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/5 via-card to-accent/5 p-6">
        <h2 className="font-serif text-2xl">Your own topic</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Anything GMAT-related — Maya will teach it from scratch in the Tutor.
        </p>
        <form
          className="mt-4 flex flex-col gap-3 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            launchCustomTutor();
          }}
        >
          <input
            type="text"
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            placeholder="e.g. combinatorics with repetition, assumption questions, DS geometry…"
            className="flex-1 rounded-xl border border-border/60 bg-background/80 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            type="submit"
            disabled={!customTopic.trim()}
            className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-medium text-background disabled:opacity-40"
          >
            Start tutoring <ArrowRight className="size-4" />
          </button>
        </form>
      </section>

      {/* Maya recommends */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-accent" />
          <h2 className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Recommended for you
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {recommends.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => launchTutor(r)}
              className={cn(
                "group text-left rounded-2xl border border-primary/30 p-6",
                "bg-gradient-to-br from-primary/5 via-card to-accent/5",
                "transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-primary">
                  {GMAT_SECTION_LABELS[r.section]}
                </div>
                {r.mastery != null && (
                  <div className="text-xs text-muted-foreground">{r.mastery}% mastery</div>
                )}
              </div>
              <div className="mt-3 font-serif text-2xl leading-tight">{r.name}</div>
              {r.recent && (
                <div className="mt-2 text-sm text-primary">{r.recent}</div>
              )}
              <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.description}</div>
              <div className="mt-4 flex items-center text-sm font-medium text-primary">
                Start tutoring session{" "}
                <ArrowRight className="ml-1 size-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Search + filter */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all topics…"
            className="w-full rounded-full border border-border/60 bg-card py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(["all", "quant", "verbal", "di"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSection(s)}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                section === s
                  ? "bg-foreground text-background"
                  : "border border-border/60 text-muted-foreground hover:text-foreground",
              )}
            >
              {s === "all" ? "All" : GMAT_SECTION_LABELS[s].replace(" Reasoning", "")}
            </button>
          ))}
        </div>
      </div>

      {searchQuery ? (
        <section className="space-y-4">
          <h2 className="font-serif text-2xl">Search results ({filtered.length})</h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t) => (
              <TopicCard key={t.id} topic={t} onLaunch={launchTutor} />
            ))}
          </div>
        </section>
      ) : (
        <>
      {section === "all" || section === "quant" ? (
        <TopicRow title={GMAT_SECTION_LABELS.quant} topics={section === "all" ? QUANT_TOPICS : filtered.filter((t) => t.section === "quant")} onLaunch={launchTutor} />
      ) : null}
      {section === "all" || section === "verbal" ? (
        <TopicRow title={GMAT_SECTION_LABELS.verbal} topics={section === "all" ? VERBAL_TOPICS : filtered.filter((t) => t.section === "verbal")} onLaunch={launchTutor} />
      ) : null}
      {section === "all" || section === "di" ? (
        <TopicRow title={GMAT_SECTION_LABELS.di} topics={section === "all" ? DI_TOPICS : filtered.filter((t) => t.section === "di")} onLaunch={launchTutor} />
      ) : null}
        </>
      )}

      {searchQuery && filtered.length === 0 && (
        <p className="text-center text-sm text-muted-foreground">
          No topics match "{searchQuery}". Try your own topic above, or{" "}
          <button type="button" className="text-primary underline" onClick={() => askMaya(searchQuery)}>
            ask Maya in chat
          </button>
          .
        </p>
      )}

      {/* Mindset */}
      {!searchQuery && (section === "all" || section === "mindset") && (
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-3xl font-normal tracking-tight">{GMAT_SECTION_LABELS.mindset}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The conversations a human tutor has — voice-native, step by step.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {MINDSET_TOPICS.map((m) => {
              const Icon = MINDSET_ICONS[m.id] ?? Brain;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => launchTutor(m)}
                  className={cn(
                    "group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left",
                    "transition-all hover:-translate-y-0.5 hover:shadow-md",
                  )}
                >
                  <div className="grid size-10 place-items-center rounded-xl bg-accent/15 text-accent">
                    <Icon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-tight">{m.name}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{m.description}</div>
                  </div>
                  <ArrowRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      <p className="text-center text-xs text-muted-foreground">
        {ALL_GMAT_TOPICS.length} topics across Quant, Verbal & Data Insights
      </p>

      {/* Ask Maya — chatbot, NOT tutor */}
      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = askQuery.trim();
            if (q) {
              askMaya(q);
              setAskQuery("");
            }
          }}
          className={cn(
            "pointer-events-auto flex w-full max-w-2xl items-center gap-2",
            "rounded-full border border-border bg-card/95 backdrop-blur-md",
            "p-1.5 shadow-2xl shadow-foreground/10",
          )}
        >
          <Mic className="ml-3 size-4 text-muted-foreground" />
          <input
            type="text"
            value={askQuery}
            onChange={(e) => setAskQuery(e.target.value)}
            placeholder="Quick question? Ask Maya (chatbot)…"
            className="flex-1 bg-transparent px-2 py-2 text-sm placeholder:text-muted-foreground focus:outline-none"
          />
          <button
            type="submit"
            disabled={!askQuery.trim()}
            className={cn(
              "flex h-9 items-center gap-1 rounded-full px-4 text-xs font-medium transition-all",
              askQuery.trim()
                ? "bg-foreground text-background hover:bg-foreground/90"
                : "bg-secondary text-muted-foreground",
            )}
          >
            Ask Maya <ArrowRight className="size-3" />
          </button>
        </form>
      </div>
    </div>
  );
}

function TopicRow({
  title,
  topics,
  onLaunch,
}: {
  title: string;
  topics: GmatTopic[];
  onLaunch: (topic: GmatTopic) => void;
}) {
  if (topics.length === 0) return null;
  const avg = Math.round(
    topics.reduce((s, t) => s + (t.mastery ?? 0), 0) / topics.length,
  );
  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif text-3xl font-normal tracking-tight">{title}</h2>
        <div className="text-xs text-muted-foreground">avg mastery {avg}%</div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {topics.map((t) => (
          <TopicCard key={t.id} topic={t} onLaunch={onLaunch} />
        ))}
      </div>
    </section>
  );
}

function TopicCard({
  topic,
  onLaunch,
}: {
  topic: GmatTopic;
  onLaunch: (topic: GmatTopic) => void;
}) {
  const [showResources, setShowResources] = useState(false);
  const related = resourcesForTopic(topic.id);
  const color =
    (topic.mastery ?? 50) < 50
      ? "bg-destructive"
      : (topic.mastery ?? 50) < 75
        ? "bg-accent"
        : "bg-success";

  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card transition-all",
        showResources && "ring-1 ring-primary/30",
      )}
    >
      <button
        type="button"
        onClick={() => onLaunch(topic)}
        className="group flex w-full flex-col gap-3 p-5 text-left hover:bg-secondary/20"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-base font-medium leading-tight">{topic.name}</div>
          <ArrowRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">{topic.description}</p>
        {topic.recent && <div className="text-xs text-primary">{topic.recent}</div>}
        {topic.mastery != null && (
          <div className="mt-auto space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Mastery</span>
              <span className="font-mono font-medium">{topic.mastery}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
              <div className={cn("h-full transition-all", color)} style={{ width: `${topic.mastery}%` }} />
            </div>
          </div>
        )}
      </button>
      <div className="flex border-t border-border/60 px-5 py-2">
        <button
          type="button"
          onClick={() => setShowResources((s) => !s)}
          className="text-xs font-medium text-primary hover:underline"
        >
          {showResources ? "Hide" : "Show"} curated resources
        </button>
      </div>
      {showResources && (
        <div className="space-y-3 border-t border-border/60 px-5 pb-5 pt-3">
          <p className="text-[11px] text-muted-foreground">
            Maya teaches this live in the Tutor — or browse external picks:
          </p>
          <div className="space-y-4">
            {related.videos.map((v) => (
              <VideoCard key={v.id} {...v} compact />
            ))}
          </div>
          {related.articles.map((a) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noreferrer noopener"
              className="block rounded-lg bg-secondary/40 p-2 text-xs hover:bg-secondary/60"
            >
              <span className="font-medium">{a.source}</span>
              <span className="text-muted-foreground"> — {a.title}</span>
            </a>
          ))}
          {related.threads.map((c) => (
            <a
              key={c.id}
              href={c.url}
              target="_blank"
              rel="noreferrer noopener"
              className="block rounded-lg bg-secondary/40 p-2 text-xs hover:bg-secondary/60"
            >
              <span className="font-medium">{c.source}</span>
              <span className="text-muted-foreground"> — {c.title}</span>
            </a>
          ))}
          <button
            type="button"
            onClick={() => onLaunch(topic)}
            className="w-full rounded-lg border border-primary/30 py-2 text-xs font-medium text-primary hover:bg-primary/5"
          >
            Start tutoring — {topic.name}
          </button>
        </div>
      )}
    </div>
  );
}

// Re-export for resource tag mapping
export { getTopicById };
